import React, { useEffect, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, RotateCw, Loader2, Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { getAttributionTokens } from '../lib/shopSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Traduce in italiano on-brand i messaggi d'errore grezzi di Stripe (spesso in inglese).
const itPaymentError = (raw) => {
  if (!raw) return 'Non riuscito';
  const s = String(raw).toLowerCase();
  if (s.includes('authentication') || s.includes('requires_action') || s.includes('3d')) {
    return 'Autenticazione richiesta dalla banca: riprova e conferma il pagamento';
  }
  if (s.includes('insufficient')) return 'Fondi insufficienti sulla carta';
  if (s.includes('expired')) return 'Carta scaduta';
  if (s.includes('incorrect') || s.includes('invalid')) return 'Dati della carta non validi';
  if (s.includes('declin')) return 'Pagamento rifiutato dalla banca';
  return 'Pagamento non riuscito, riprova';
};


const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#008080',
    colorText: '#1e293b',
    colorDanger: '#dc2626',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    borderRadius: '8px',
  },
};

/**
 * Form Stripe interno: unica Payment Element per SetupIntent.
 * Quando confermato, ritorna il payment_method salvato per fare le charge.
 */
const SetupForm = ({ clientSecret, onSetupComplete, cartTotal, itemsCount, craftersCount, hasCustomItem, discountAmount = 0 }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [recessoConsent, setRecessoConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (hasCustomItem && !recessoConsent) {
      setErrorMsg('Per procedere devi confermare di aver compreso che il diritto di recesso è escluso per i prodotti su misura.');
      return;
    }
    setProcessing(true);
    setErrorMsg(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMsg(submitError.message || 'Dati carta non validi');
        setProcessing(false);
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/order-success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMsg(error.message || 'Autenticazione carta fallita');
        toast.error(error.message || 'Autenticazione carta fallita');
        setProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        // Passa al parent per fare le charge multiple
        await onSetupComplete(setupIntent.payment_method);
      } else {
        setErrorMsg('Impossibile completare la validazione della carta');
        setProcessing(false);
      }
    } catch (err) {
      console.error('confirmSetup error:', err);
      setErrorMsg(err?.message || 'Errore imprevisto');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Riepilogo totale (in cima, ben visibile) */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-slate-600">Totale ordine</span>
          <span className="text-2xl font-bold text-shop-primary">€{Number(cartTotal || 0).toFixed(2)}</span>
        </div>
        {Number(discountAmount || 0) > 0 && (
          <div className="flex items-center justify-between text-sm text-emerald-700 font-medium mb-1" data-testid="express-discount-line">
            <span>Sconto applicato</span>
            <span>-€{Number(discountAmount).toFixed(2)}</span>
          </div>
        )}
        <p className="text-xs text-slate-500">
          {itemsCount} {itemsCount === 1 ? 'articolo' : 'articoli'} · {craftersCount}{' '}
          {craftersCount === 1 ? 'artigiano' : 'artigiani'}
        </p>
        {craftersCount > 1 && (
          <p className="text-xs text-teal-700 mt-2 leading-relaxed">
            💡 Un solo pagamento: dividiamo automaticamente l&apos;importo tra gli
            artigiani. Ognuno riceve la sua parte direttamente sul suo conto.
          </p>
        )}
      </div>

      {/* Payment Element */}
      <div className="bg-white rounded-xl border p-4">
        <PaymentElement options={{
          layout: 'tabs',
          defaultValues: { billingDetails: { address: { country: 'IT' } } },
        }} />
      </div>

      {/* Error */}
      {errorMsg && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {errorMsg}
        </div>
      )}

      {/* Consenso esclusione recesso (solo prodotti su misura) */}
      {hasCustomItem && (
        <label
          className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 cursor-pointer"
          data-testid="recesso-consent-label"
        >
          <input
            type="checkbox"
            checked={recessoConsent}
            onChange={(e) => setRecessoConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-shop-primary"
            data-testid="recesso-consent-checkbox"
          />
          <span>
            Confermo di aver compreso che, trattandosi di un prodotto realizzato su misura,
            il diritto di recesso è escluso ai sensi dell&apos;art. 59 del Codice del Consumo.
          </span>
        </label>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={!stripe || processing || (hasCustomItem && !recessoConsent)}
        className="w-full bg-shop-primary hover:bg-shop-primary/90 text-white py-6 text-base font-semibold"
        data-testid="single-payment-submit-btn"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Elaborazione in corso...
          </>
        ) : (
          <>
            <Lock className="w-4 h-4 mr-2" />
            Paga €{Number(cartTotal || 0).toFixed(2)}
          </>
        )}
      </Button>

      <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Pagamento sicuro cifrato tramite Stripe
      </p>
    </form>
  );
};

/**
 * Blocco pagamento unificato:
 * 1. Chiede setup-intent al backend
 * 2. Mostra ONE Payment Element per raccogliere la carta
 * 3. Al confirmSetup → chiama /multi-charge → mostra riepilogo per crafter
 * 4. Se tutto ok → onAllSucceeded(results). Se parziale → mostra retry
 */
export const SingleStripePayment = ({
  stripePromise,
  cartItems,
  buyer,
  shippingAddress,
  shippingBreakdown,
  shippingCost,
  onAllSucceeded,
  onPartialSuccess,
  discountCode = null,
  discountAmount = 0,
  shippingMethod = 'standard',
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);
  const [chargeResults, setChargeResults] = useState(null); // {order_id, results, all_succeeded}
  const [retryingCrafter, setRetryingCrafter] = useState(null);

  const subtotal = cartItems.reduce(
    (sum, it) => sum + Number(it.totalPrice ?? it.price ?? 0) * Number(it.quantity || 1),
    0
  );
  // Se il parent passa un shippingCost totale usalo, altrimenti somma dal breakdown
  const totalShipping = Number(
    shippingCost ??
      (Array.isArray(shippingBreakdown?.breakdown)
        ? shippingBreakdown.breakdown.reduce((s, r) => s + Number(r?.shipping_cost || 0), 0)
        : shippingBreakdown?.total_shipping ?? 0)
  );
  const cartTotal = Math.max(0, subtotal + totalShipping - Number(discountAmount || 0));
  const craftersSet = new Set(cartItems.map((i) => i.crafterId || i.crafter?.id).filter(Boolean));
  const craftersCount = craftersSet.size;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/checkout/setup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buyer_email: buyer?.email,
            buyer_id: buyer?.id,
          }),
        });
        const data = await r.json();
        if (!r.ok) {
          // Logga il dettaglio tecnico (es. errori Stripe) solo in console;
          // all'utente mostra un messaggio chiaro e rassicurante in italiano.
          console.error('setup-intent error:', data?.detail || r.status);
          throw new Error('Non è stato possibile avviare il pagamento in questo momento. Riprova tra poco: se il problema persiste, contatta l’assistenza.');
        }
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setCustomerId(data.customer_id);
      } catch (e) {
        if (!cancelled) setInitError(e.message || 'Non è stato possibile avviare il pagamento. Riprova tra poco.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildMultiChargePayload = (paymentMethodId) => {
    // Ripartisci lo shipping cost tra i crafter (una sola volta per crafter).
    // shippingBreakdown.breakdown è [{crafter_id, shipping_cost, ...}].
    const shippingPerCrafter = {};
    if (Array.isArray(shippingBreakdown?.breakdown)) {
      for (const row of shippingBreakdown.breakdown) {
        if (row?.crafter_id) {
          shippingPerCrafter[row.crafter_id] = Number(row.shipping_cost || 0);
        }
      }
    }
    // Fallback: se non c'è breakdown ma un totale, spalmalo uniformemente sui crafter
    if (Object.keys(shippingPerCrafter).length === 0 && totalShipping > 0 && craftersCount > 0) {
      const perCrafter = totalShipping / craftersCount;
      for (const cid of craftersSet) shippingPerCrafter[cid] = perCrafter;
    }
    const seenShipping = new Set();
    return {
      payment_method_id: paymentMethodId,
      customer_id: customerId,
      buyer_id: buyer?.id || null,
      buyer_email: buyer?.email || null,
      shipping_address: shippingAddress || {},
      currency: 'eur',
      discount_code: discountCode || null,
      shipping_method: shippingMethod || 'standard',
      // Il server ricalcola gli importi dal DB: se il totale non coincide con quello
      // mostrato qui, non addebita nulla e chiede di ricaricare.
      expected_total: Math.round(cartTotal * 100) / 100,
      // Clienti arrivati dal link di una bottega (commissione 0% per gli artigiani Pro)
      shop_attributions: getAttributionTokens(),
      items: cartItems.map((it) => {
        const cid = it.crafterId || it.crafter?.id;
        const includeShipping = cid && !seenShipping.has(cid);
        if (cid) seenShipping.add(cid);
        return {
          product_id: it.productId || it.id,
          crafter_id: cid,
          price: Number(it.totalPrice ?? it.price ?? 0),
          quantity: Number(it.quantity || 1),
          title: it.title || it.name,
          shipping_cost: includeShipping ? Number(shippingPerCrafter[cid] || 0) : 0,
          image: it.image || null,
          selected_variants: it.selectedVariants || {},
          selected_customizations: it.selectedCustomizations || {},
          customization_values: it.customizationValues || {},
          customer_attachments: it.customerAttachments || {},
          is_custom_order: !!it.isCustomOrder,
          request_id: it.requestId || null,
          proposal_id: it.proposalId || null,
        };
      }),
    };
  };

  const runMultiCharge = async (paymentMethodId) => {
    const r = await fetch(`${API_URL}/api/checkout/multi-charge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildMultiChargePayload(paymentMethodId)),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !Array.isArray(data?.results)) {
      // Nessun addebito è stato fatto (es. totale cambiato, prodotto non più disponibile):
      // l'errore viene mostrato nel form di pagamento.
      throw new Error(
        typeof data?.detail === 'string'
          ? data.detail
          : 'Non è stato possibile completare il pagamento. Non ti è stato addebitato nulla: riprova tra poco.'
      );
    }
    setChargeResults(data);
    if (data.all_succeeded) {
      await onAllSucceeded?.(data);
    } else {
      await onPartialSuccess?.(data);
    }
    return data;
  };

  const handleRetryFailed = async (crafterId) => {
    // Riprova la charge fallita usando lo stesso payment method già raccolto
    // (Semplice: chiama di nuovo /multi-charge con solo gli items falliti)
    if (!chargeResults) return;
    const failedEntry = chargeResults.results.find((r) => r.crafter_id === crafterId);
    if (!failedEntry) return;

    setRetryingCrafter(crafterId);
    try {
      // Serve un nuovo SetupIntent per raccogliere una nuova carta
      // (in questa versione, forziamo il reload della pagina per semplicità)
      window.location.reload();
    } finally {
      setRetryingCrafter(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-shop-primary mb-3" />
        <p className="text-sm text-slate-600">Preparazione pagamento sicuro...</p>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">{initError}</p>
      </div>
    );
  }

  // Se ho risultati (dopo la charge), mostro il riepilogo
  if (chargeResults) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="results"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-4"
          data-testid="charge-results"
        >
          {chargeResults.all_succeeded ? (
            <div className="text-center py-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12 }}
                className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </motion.div>
              <h3 className="font-bold text-slate-800">Pagamento completato!</h3>
              <p className="text-sm text-slate-600 mt-1">Stiamo preparando il tuo ordine...</p>
            </div>
          ) : (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="font-semibold text-amber-900 text-sm mb-1">
                  Pagamento parzialmente completato
                </p>
                <p className="text-xs text-amber-800">
                  Alcuni pagamenti sono andati a buon fine, altri hanno avuto problemi.
                  Nessun addebito duplicato.
                </p>
              </div>
              <div className="space-y-2">
                {chargeResults.results.map((r) => (
                  <div
                    key={r.crafter_id}
                    className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${
                      r.status === 'succeeded'
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                    data-testid={`charge-result-${r.crafter_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {r.status === 'succeeded' ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {r.crafter_name}
                        </p>
                        <p className="text-xs text-slate-600">
                          €{Number(r.amount).toFixed(2)} ·{' '}
                          {r.status === 'succeeded' ? 'Pagato' : itPaymentError(r.error)}
                        </p>
                      </div>
                    </div>
                    {r.status !== 'succeeded' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetryFailed(r.crafter_id)}
                        disabled={retryingCrafter === r.crafter_id}
                        data-testid={`retry-charge-${r.crafter_id}`}
                      >
                        {retryingCrafter === r.crafter_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <RotateCw className="w-3.5 h-3.5 mr-1" />
                            Riprova
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = '/dashboard?tab=cliente&section=active-orders'}
              >
                Vai ai miei ordini
              </Button>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div data-testid="single-stripe-payment">
      {stripePromise && clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance, locale: 'it' }}>
          <SetupForm
            clientSecret={clientSecret}
            onSetupComplete={runMultiCharge}
            cartTotal={cartTotal}
            itemsCount={cartItems.length}
            craftersCount={craftersCount}
            hasCustomItem={cartItems.some((i) => i.isCustomOrder || i.fulfillmentType === 'PERSONALIZED' || (i.selectedCustomizations && Object.keys(i.selectedCustomizations).some((k) => i.selectedCustomizations[k])))}
            discountAmount={discountAmount}
          />
        </Elements>
      ) : (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Stripe non configurato. Imposta la Publishable Key in SuperAdmin → Connettori → Stripe.
        </div>
      )}
    </div>
  );
};

export default SingleStripePayment;
